import { InMemoryCache } from '@apollo/client/cache/inmemory/inMemoryCache';
import { ApolloClient } from '@apollo/client/core/ApolloClient';
import { from } from '@apollo/client/link/core';
import { onError } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';

import { GraphQLNetworkError, GraphQLError } from './errors';

const httpLink = new HttpLink({
  uri:
    import.meta.env.VITE_STRAPI_GRAPHQL_URL ||
    'https://cms.api.staging.pimster.app/graphql',
});

const getLink = async () => {
  if (import.meta.env.SSR) {
    const Sentry = await import('@sentry/node');
    const errorLink = onError(
      ({ operation, graphQLErrors, networkError, response }) => {
        Sentry.withScope((scope) => {
          const breadcrumb = {
            category: 'GraphQL',
            message: operation.operationName || 'Unnamed operation',
            data: {
              variables: operation.variables,
              query: operation.query.loc?.source.body,
            },
            response,
          };

          scope.addBreadcrumb(breadcrumb);

          if (graphQLErrors) {
            graphQLErrors.forEach((exception) => {
              console.error(
                `[GraphQL error]:\n\n Operation: ${JSON.stringify(
                  operation
                )}\n\nMessage: ${exception.message}\nLocation: ${JSON.stringify(
                  exception.locations
                )}\n\nPath: ${JSON.stringify(
                  exception.path
                )}\n\nExtensions: ${JSON.stringify(exception.extensions)}`
              );
              scope.captureException(new GraphQLError(exception.message));
            });
          }
          if (networkError) {
            console.error(`[Network error]: ${networkError}`);
            const { message, ...details } = networkError;
            scope.addBreadcrumb({ category: 'GraphQL', ...details });
            scope.captureException(new GraphQLNetworkError(message));
          }
        });
      }
    );
    return from([errorLink, httpLink]);
  } else {
    const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach((exception) => {
          console.error(
            `[GraphQL error]:\n\n Operation: ${JSON.stringify(
              operation
            )}\n\nMessage: ${exception.message}\nLocation: ${JSON.stringify(
              exception.locations
            )}\n\nPath: ${JSON.stringify(
              exception.path
            )}\n\nExtensions: ${JSON.stringify(exception.extensions)}`
          );
          throw new GraphQLError(exception.message);
        });
      }
      if (networkError) {
        console.error(`[Network error]: ${networkError}`);
        const { message } = networkError;
        throw new GraphQLNetworkError(message);
      }
    });
    return from([errorLink, httpLink]);
  }
};

export const makeClient = async ({ ssr }: { ssr: boolean }) => {
  const cache = new InMemoryCache();
  const link = await getLink();
  return new ApolloClient({
    cache,
    link,
    ssrMode: ssr,
  });
};
